import { useState, useRef, useEffect, FC, useMemo, Fragment } from 'react';
import RightArrowAlt from 'assets/svg/RightArrowAlt';
import { getIconByName } from 'utils/helperMethods';
import _ from 'lodash';
import { queryClient } from 'services/reactQueryService';
import { PricingSimulatorQueryEnum } from 'utils/queryKeysEnum';
import AttributesPanel from './AttributesPanel';

type Props = {
    disabled?: boolean;
    availableFilters: any[];
    allCheckedAttribute: any;
    allSelectedValueForAllAttribute: any;
    applyFilterObject: any;
    resetFilterObject: (triggeredFromClick?: boolean) => void;
    onSubmitHandler: (obj: any) => void;
    showAttributePanelToLeft?: boolean;
    url: string;
    selectedFilter: any;
    setSelectedFilter: Function;
    setTempApplyFilterObject: Function;
    tempApplyFilterObject: any;
    apiVariables: any;
    filterDimensionInfo: any;
    filterDrpDwnClassName?: string;
    dataParser?: (data: any) => { label: string; value: string }[];
    filterIcon?: React.ReactNode;
};

const NewGlobalFilter: FC<Props> = ({
    disabled,
    availableFilters,
    allCheckedAttribute,
    allSelectedValueForAllAttribute,
    applyFilterObject,
    resetFilterObject,
    onSubmitHandler,
    showAttributePanelToLeft = false,
    url,
    setTempApplyFilterObject,
    tempApplyFilterObject,
    apiVariables,
    selectedFilter,
    setSelectedFilter,
    filterDimensionInfo,
    filterDrpDwnClassName = 'w-20',
    dataParser,
    filterIcon,
}) => {
    const [tempAllCheckedAttribute, setTempAllCheckedAttribute] = useState<any>({});
    const [tempAllSelectedValueForAllAttribute, setTempAllSelectedValueForAllAttribute] = useState<any>({});
    const [filterSelectionVisible, setFilterSelectionVisible] = useState(false);
    const [attributePanelVisible, setAttributePanelVisible] = useState(false);
    const selectionPanelRef = useRef<any>(null);
    const attributePanelRef = useRef<any>(null);

    const [searchKey, setSearchKey] = useState('');
    const [offset, setOffset] = useState(0);

    const onOpenFilterClick = () => {
        if (disabled) return;
        setFilterSelectionVisible(true);
        setTempAllCheckedAttribute(_.cloneDeep(allCheckedAttribute));
        setTempApplyFilterObject(_.cloneDeep(applyFilterObject));
        setTempAllSelectedValueForAllAttribute(_.cloneDeep(allSelectedValueForAllAttribute));
    };

    const handleSelectedFilter = (filter: any) => {
        queryClient.removeQueries({
            queryKey: [PricingSimulatorQueryEnum.FILTER_ATTRIBUTES],
            exact: false,
        });
        queryClient.removeQueries({
            queryKey: ['group-attributes'],
            exact: false,
        });

        setSelectedFilter(filter);
        setAttributePanelVisible(true);
        setOffset(0);
        setSearchKey('');
    };

    const handleSubmit = () => {
        setFilterSelectionVisible(false);
        setSelectedFilter(null);
        setAttributePanelVisible(false);

        const result: any = {
            allCheckedAttribute: _.cloneDeep(tempAllCheckedAttribute),
            allSelectedValueForAllAttribute: _.cloneDeep(tempAllSelectedValueForAllAttribute),
            applyFilterObject: _.cloneDeep(tempApplyFilterObject),
            // showIsEditedRecords: tempShowIsEditedRecords,
        };
        onSubmitHandler(result);
    };

    // both for parent and child component if clicked outside close the panels
    const handleClickOutside = (event: any) => {
        if (
            (selectionPanelRef.current &&
                !selectionPanelRef.current?.contains(event.target) &&
                attributePanelRef.current &&
                !attributePanelRef.current?.contains(event.target)) ||
            (!attributePanelRef.current &&
                selectionPanelRef.current &&
                !selectionPanelRef.current?.contains(event.target))
        ) {
            resetInitialState();
        }
    };

    const resetInitialState = () => {
        setFilterSelectionVisible(false);
        setAttributePanelVisible(false);
        setSelectedFilter(null);
    };

    // clearing filter selection made
    const onResetClick = () => {
        resetInitialState();
        resetFilterObject(true);
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    //separates list of attribute types depending on dimension
    function separateByDimension(arr: any) {
        const result: any = {};
        arr.forEach((item: any) => {
            const dimension = item.dimension;

            if (result && !(dimension in result)) {
                result[dimension] = {
                    label: filterDimensionInfo?.[dimension]?.label,
                    items: [],
                };
            }

            result[dimension]?.items?.push(item);
        });

        return result;
    }

    //use this variable just to display in UI
    const updatedAvailableFilters = useMemo(() => {
        return separateByDimension(availableFilters);
    }, [availableFilters]);

    return (
        <div className="relative">
            {/* MAIN BUTTON */}
            <span
                className={`border flex justify-between items-center py-1 px-3 cursor-pointer rounded-[6px] ${
                    disabled
                        ? 'cursor-not-allowed border-borderGrey bg-backgroundGreyAlt6  text-promoColour'
                        : 'cursor-pointer'
                } ${
                    filterSelectionVisible ? ' border-purpleFont text-purpleFont' : ' border-borderGreyAl1  '
                }  ${filterDrpDwnClassName}  `}
                onClick={onOpenFilterClick}
            >
                {filterIcon || getIconByName('FilterFilled')}
                <span>Filter</span>
            </span>

            {/* SELECTION PANEL */}
            {filterSelectionVisible && (
                <div
                    className={`absolute top-9 p-3 bg-white z-50 shadow-[0px_9px_28px_8px_rgba(0,0,0,0.05)] rounded-lg left-0 w-[258px] flex flex-col items-start  left-[-178px] `}
                    ref={selectionPanelRef}
                >
                    {/* all attributes */}
                    <>
                        <div className="w-full max-h-[348px] overflow-auto">
                            {Object.keys(updatedAvailableFilters)?.map((dimension: any) => {
                                const label = updatedAvailableFilters?.[dimension]?.label || '';
                                const items = updatedAvailableFilters?.[dimension]?.items || [];
                                return (
                                    <Fragment key={dimension}>
                                        <div className="flex my-3 items-center">
                                            <p className="text-promoColour font-xs mr-2">{label}</p>
                                            <p className="w-full flex-1 border-b border-b-backgroundLightGrey"></p>
                                        </div>
                                        {items.map((filter: any) => {
                                            return (
                                                <div
                                                    key={filter?.value}
                                                    className={` rounded cursor-pointer flex items-center ${
                                                        filter?.value === selectedFilter?.value
                                                            ? 'bg-bgBackground text-purpleFont'
                                                            : ''
                                                    }`}
                                                >
                                                    <div
                                                        className="w-full flex gap-2 justify-between items-center py-2 px-3 rounded"
                                                        onClick={() => handleSelectedFilter(filter)}
                                                    >
                                                        <div className="w-full flex gap-2 font-xs items-center  ">
                                                            <span> {filter?.label}</span>
                                                        </div>
                                                        {filter.value !== selectedFilter?.value && <RightArrowAlt />}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </Fragment>
                                );
                            })}
                        </div>
                    </>

                    {/* BUTTONS */}
                    <div className="flex gap-2 items-center justify-end  mt-2 pt-2 w-full border-t border-backgroundGreyAlt1">
                        <span
                            className="cursor-pointer py-1 px-3 rounded border border-borderGreyAl1"
                            onClick={onResetClick}
                        >
                            Reset
                        </span>
                        <span
                            onClick={handleSubmit}
                            className="bg-purpleFont font-xs cursor-pointer py-1 px-3 text-white rounded"
                        >
                            Apply Filter
                        </span>
                    </div>
                </div>
            )}
            {/* ATTRIBUTES PANEL */}
            {attributePanelVisible && selectedFilter && (
                <AttributesPanel
                    searchKey={searchKey}
                    setSearchKey={setSearchKey}
                    offset={offset}
                    setOffset={setOffset}
                    selectedFilter={selectedFilter}
                    tempApplyFilterObject={tempApplyFilterObject}
                    setTempApplyFilterObject={setTempApplyFilterObject}
                    tempAllCheckedAttribute={tempAllCheckedAttribute}
                    setTempAllCheckedAttribute={setTempAllCheckedAttribute}
                    tempAllSelectedValueForAllAttribute={tempAllSelectedValueForAllAttribute}
                    setTempAllSelectedValueForAllAttribute={setTempAllSelectedValueForAllAttribute}
                    ref={attributePanelRef}
                    showAttributePanelToLeft={showAttributePanelToLeft}
                    url={url}
                    variables={apiVariables}
                    dataParser={dataParser}
                />
            )}
        </div>
    );
};
export default NewGlobalFilter;
